class TabsSectionDesktop extends HTMLElement {
    constructor() {
        super()
        this.activeIndex = 0
    }

    connectedCallback() {
        this.triggers = Array.from(this.querySelectorAll("[data-ts-trigger]"))
        this.panelGroups = [
            Array.from(this.querySelectorAll("[data-ts-media]")),
            Array.from(this.querySelectorAll("[data-ts-description]")),
        ]

        this.triggers.forEach((trigger, index) => {
            trigger.addEventListener("mouseenter", () => this.activate(index))
            trigger.addEventListener("focus", () => this.activate(index))
        })

        this.activate(0)
    }

    activate(index) {
        if (index < 0 || index >= this.triggers.length) return

        this.activeIndex = index

        this.triggers.forEach((trigger, i) => {
            trigger.classList.toggle("active", i === index)
        })

        this.panelGroups.forEach((group) => {
            group.forEach((panel, i) => {
                panel.classList.toggle("active", i === index)
            })
        })
    }
}

class TabsSectionMobile extends HTMLElement {
    constructor() {
        super()
        this.activeIndex = -1
        this.scrollTicking = false
    }

    connectedCallback() {
        this.panelGroups = [
            Array.from(this.querySelectorAll("[data-ts-mobile-panel]")),
            Array.from(this.querySelectorAll("[data-ts-mobile-trigger]")),
            Array.from(this.querySelectorAll("[data-ts-mobile-description]")),
        ]
        this.markers = Array.from(
            this.querySelectorAll("[data-ts-mobile-marker]")
        )
        this.progressFill = this.querySelector("[data-ts-mobile-progress-fill]")
        this.sticky = this.querySelector("[data-ts-mobile-sticky]")

        this.onScroll = this.onScroll.bind(this)
        this.onResize = this.onResize.bind(this)

        this.measureViewport()

        window.addEventListener("scroll", this.onScroll, { passive: true })
        window.addEventListener("resize", this.onResize)
        window.addEventListener("orientationchange", this.onResize)

        this.setActive(0)
        this.onScroll()
        this.setupParallax()
    }

    disconnectedCallback() {
        window.removeEventListener("scroll", this.onScroll)
        window.removeEventListener("resize", this.onResize)
        window.removeEventListener("orientationchange", this.onResize)
        if (this.parallaxStop) this.parallaxStop.forEach((off) => off())
        if (this.parallaxFrame) cancelAnimationFrame(this.parallaxFrame)
    }

    measureViewport() {
        this.viewportWidth = window.innerWidth
        const measured = this.sticky ? this.sticky.offsetHeight : 0
        this.pinHeight = measured || window.innerHeight
    }

    onResize() {
        if (window.innerWidth === this.viewportWidth) return
        this.measureViewport()
        this.updateFromScroll()
    }

    onScroll() {
        if (this.scrollTicking) return
        this.scrollTicking = true
        requestAnimationFrame(() => {
            this.updateFromScroll()
            this.scrollTicking = false
        })
    }

    setActive(index) {
        if (index < 0 || index === this.activeIndex) return
        this.activeIndex = index
        this.panelGroups.forEach((group) => {
            group.forEach((el, i) => {
                el.classList.toggle("active", i === index)
            })
        })
    }

    updateFromScroll() {
        const slideCount = this.panelGroups[0].length
        if (!slideCount) return

        const pinnedDistance = this.offsetHeight - this.pinHeight
        let fraction = 1

        if (pinnedDistance > 0) {
            const scrolled = -this.getBoundingClientRect().top
            fraction = Math.max(0, Math.min(1, scrolled / pinnedDistance))
        }

        const index = Math.min(
            slideCount - 1,
            Math.floor(fraction * slideCount)
        )
        this.setActive(index)

        if (this.progressFill) {
            this.progressFill.style.width = `${fraction * 100}%`
        }
    }

    setupParallax() {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches)
            return

        const clamp01 = (n) => Math.min(1, Math.max(0, n))
        const lerp = (a, b, t) => a + (b - a) * t
        const damping = 0.08

        const items = this.panelGroups[0]
            .map((panel, i) => ({
                el: panel,
                marker: this.markers[i],
                amount: Number(panel.dataset.amount) || 10,
                current: 0,
                target: 0,
            }))
            .filter((item) => item.marker)

        if (!items.length) return

        const measure = () => {
            const vh = this.pinHeight
            items.forEach((item) => {
                const rect = item.marker.getBoundingClientRect()
                const progress = clamp01((vh - rect.top) / (vh + rect.height))
                item.target = (progress - 0.5) * 2 * item.amount
            })
        }

        const paint = () => {
            items.forEach((item) => {
                item.current = lerp(item.current, item.target, damping)
                item.el.style.transform = `translate3d(0, ${item.current.toFixed(2)}px, 0)`
            })
        }

        if (window.themeTicker) {
            this.parallaxStop = [
                window.themeTicker.read(measure),
                window.themeTicker.write(paint),
            ]
            return
        }

        const frame = () => {
            measure()
            paint()
            this.parallaxFrame = requestAnimationFrame(frame)
        }

        this.parallaxFrame = requestAnimationFrame(frame)
    }
}

customElements.define("tabs-section-desktop", TabsSectionDesktop)
customElements.define("tabs-section-mobile", TabsSectionMobile)
